import { useMemo, useState } from "react";
import type {
  DropListWithIconItem,
  GroupedBalancesByAccount,
  Order,
  PreparedOrder,
  TradePathData,
  HubTradePrepareOrderBody,
  HubTradeCreateOrderBody,
} from "@cede/types";
import { CAMPAIGN_PLACEHOLDER_NAME, OrderStatus, OrderType } from "@cede/types";
import Decimal from "decimal.js";
import {
  API_ENDPOINT,
  QueryKeys,
  forgeTokenURL,
  getCurrentRate,
  getErrorMessage,
  getExchangeLogo,
  getTokensFromPairSymbol,
  orderToTransactionMapper,
  Precise,
  SdkErrorCodes,
  unformatNumberAsString,
} from "@cede/utils";
import { useTradeForm } from "../form/useTradeForm";
import { useVerifyOrder } from "../utils/useVerifyOrder";
import {
  MultipleTransactionsPreparedOrder,
  MultipleTransactionsPreparedWithdrawal,
  useMultipleTransactionsStore,
} from "./useMultipleTransactionsStore";
import { useAccounts } from "@/hooks/accounts/useAccounts";
import { useApi } from "@/hooks/core/useApi";
import { useSupportedExchanges } from "@/hooks/core/useSupportedExchanges";
import { useTriggerLoadingConditionally } from "@/hooks/core/useTriggerLoadingConditionally";
import { getPrice } from "@/hooks/prices";
import { usePrices } from "@/hooks/prices";
import { useHubUser } from "@/hooks/user/useHubUser";
import { AlertTypes } from "@/store";
import { useAlert } from "@/store/alert/alert";
import { useOrderBookStore } from "@/store/orderBookStore";
import { useSelectedTransaction } from "@/store/transaction/transaction";
import { getExchangeAccountBalances } from "@/utils/api";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { orderV2ToOrder } from "@/utils/exchange";




export const isMultipleTransactionsPreparedOrder = (
  transaction: MultipleTransactionsPreparedOrder | MultipleTransactionsPreparedWithdrawal,
): transaction is MultipleTransactionsPreparedOrder => {
  return "from" in transaction;
};

export const getLastOrderTransaction = (
  transactions: (MultipleTransactionsPreparedOrder | MultipleTransactionsPreparedWithdrawal)[],
) => {
  for (const transaction of [...transactions].reverse()) {
    if (isMultipleTransactionsPreparedOrder(transaction)) {
      return transaction;
    }
  }
  return null;
};

export type UseMultipleTransactionsParams = {
  accountId: string;
  fromTokenAmount: string;
  onFinish?: () => void;
  tradePath: TradePathData[] | undefined | null;
  /**
   * Only for withdrawals
   */
  activeWithdrawal?: boolean;
  whitelistedAddress?: string;
  recipientAddress?: string;
  network?: DropListWithIconItem;
  withdrawalTag?: string;
};

export const useMultipleTransactions = ({
  accountId,
  fromTokenAmount,
  tradePath,
  onFinish,
  activeWithdrawal = false,
  recipientAddress,
  network,
  withdrawalTag,
}: UseMultipleTransactionsParams) => {
  const apiHelper = useApi();
  const { auth, hubUser } = useHubUser();
  const orderBook = useOrderBookStore((state) => state.orderBook);
  const { accountsByIds } = useAccounts();
  const account = accountsByIds[accountId];
  const queryParams = new URLSearchParams(window.location.search);
  const appendAlert = useAlert((state) => state.append);

  const {
    transactions,
    setTransactions,
    clear: clearState,
    startProcess,
    hasStartedTheProcess,
  } = useMultipleTransactionsStore();
  const setExecutedTransactionData = useSelectedTransaction((state) => state.setTransaction);
  const [isTxLoading, setIsTxLoading] = useState(false);
  const { supportedExchangesById, isSupportedExchangesLoading } = useSupportedExchanges();
  useTriggerLoadingConditionally({
    isLoading: isSupportedExchangesLoading || Object.keys(supportedExchangesById).length === 0,
  });
  const { enqueueSnackbar } = useSnackbar();
  const { tradeForm } = useTradeForm();
  const config = tradeForm.initialProps;
  const { data: prices } = usePrices();
  const { verifyOrder } = useVerifyOrder();
  const queryClient = useQueryClient();

  const txTracker = useMemo(() => {
    return transactions.filter((tx) => tx.isExecuted).length;
  }, [transactions]);

  const _fitTxAmountToTokenFreeBalance = (
    amountTo: number,
    tokenSymbol: string,
    balances: GroupedBalancesByAccount,
  ) => {
    const accountBalance = balances[accountId ?? ""];
    if (!accountBalance) return amountTo;
    const tokenFreeBalance = accountBalance[tokenSymbol]?.freeBalance || 0;

    // If the amount is higher than the free balance, we need to update the amount to be sure the next transaction
    // won't fail due to insufficient balance
    return Math.min(amountTo, tokenFreeBalance);
  };

  const _awaitOrderToBeFilled = async (orderData: Order) => {
    const _handler = async (resolve: (value: Order) => unknown) => {
      if (!auth.identityToken) return;

      const order: Order = await apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_TRADE_RETRIEVE_ORDER, {
        queryParams: {
          accountId: accountId ?? "",
          orderId: orderData.id,
          pairSymbol: orderData.pairSymbol,
        },
      });

      if (order.status === OrderStatus.CLOSED) {
        resolve(order);
        return;
      }

      setTimeout(() => {
        _handler(resolve);
      }, 1000);
    };
    return new Promise(_handler);
  };

  const _getOrdersFromTradePath = async (
    executedTransactions: MultipleTransactionsPreparedOrder[],
  ): Promise<MultipleTransactionsPreparedOrder[]> => {
    if (!tradePath) return [];

    const preparedOrders: MultipleTransactionsPreparedOrder[] = executedTransactions;
    for (const path of tradePath) {
      const { pairSymbol, orderSide, from, to } = path;

      if (executedTransactions.find((tx) => isMultipleTransactionsPreparedOrder(tx) && tx.to.tokenSymbol === to)) {
        continue; // Skip already executed transactions
      }

      const accountObj = hubUser?.exchangeAccounts.find((acc) => acc.id === Number(accountId));
      if (!accountObj) continue;

      let marketRate = { bid: "0", ask: "0" };

      // Check orderBookStore cache first
      if (orderBook && orderBook.pairSymbol === pairSymbol && orderBook.exchangeId === accountObj.exchangeId) {
        marketRate = {
          bid: String(orderBook.bids[0]?.[0] || 0),
          ask: String(orderBook.asks[0]?.[0] || 0),
        };
      } else if (auth.identityToken) {
        // Fallback to API
        marketRate = await apiHelper.getOrThrow(API_ENDPOINT.HUB_TRADE_PUBLIC_MARKET_RATE, {
          queryParams: {
            pairSymbol,
            exchangeId: accountObj.exchangeId,
          },
        });
      }

      const currentRate = getCurrentRate(marketRate, orderSide);
      const amount =
        preparedOrders.length === 0
          ? String(unformatNumberAsString(fromTokenAmount))
          : preparedOrders[preparedOrders.length - 1]?.to.amount || "0";
      const baseAmount = orderSide === "sell" ? amount : Precise.stringDiv(amount, currentRate);

      if (!auth.identityToken || baseAmount === undefined) continue;

      const prepareOrderBody: HubTradePrepareOrderBody = {
        accountId: Number(accountId),
        pairSymbol,
        orderSide,
        orderType: OrderType.MARKET,
        amount: baseAmount,
        price: currentRate,
        exchangeId: accountObj.exchangeId,
      };

      const orderRequest: PreparedOrder = await apiHelper.postAuth(auth, API_ENDPOINT.HUB_TRADE_PREPARE_ORDER, {
        bodyParams: prepareOrderBody,
      });
      orderRequest.createOrderRequest.metadata = {
        tradeAndSend: true,
      };

      // Create the transaction
      preparedOrders.push({
        from: {
          amount,
          tokenSymbol: from,
          img: forgeTokenURL(from),
        },
        to: {
          amount:
            orderSide === "sell"
              ? Precise.stringMul(orderRequest.estimatedAmount, orderRequest.createOrderRequest.price) || "0"
              : orderRequest.estimatedAmount,
          tokenSymbol: to,
          img: forgeTokenURL(to),
        },
        request: orderRequest.createOrderRequest,
        estimatedFee: orderRequest.estimatedFee,
        isExecuted: false,
        exchange: {
          label: account?.accountName ?? "",
          img: getExchangeLogo(account?.exchangeId ?? ""),
        },
      });
    }

    return preparedOrders;
  };

  const _getWithdrawal = (tokenSymbol: string, amount: string): MultipleTransactionsPreparedWithdrawal => {
    const address = recipientAddress;
    return {
      network: {
        label: network?.label ?? "",
        img: network?.img ?? "",
        value: network?.value ?? "",
      },
      request: {
        accountId,
        amount: amount ?? "0",
        tokenSymbol: tokenSymbol ?? "",
        address: address ?? "",
        network: network?.value ?? "",
        withdrawalTag: withdrawalTag ?? "",
        metadata: {
          tradeAndSend: true,
          referrerSource: queryParams.get("source") ?? undefined,
          origin: window.location.origin,
        },
      },
      isExecuted: false,
      exchange: {
        label: account?.accountName ?? "",
        img: getExchangeLogo(account?.exchangeId ?? ""),
      },
    };
  };

  const _prepareTransactions = async (
    executedTransactions: (MultipleTransactionsPreparedOrder | MultipleTransactionsPreparedWithdrawal)[] = [],
  ) => {
    setIsTxLoading(true);
    try {
      // If all txs are already executed, we don't need to prepare anything
      if (executedTransactions.length === (tradePath?.length || 0) + 1) {
        setTransactions(executedTransactions);
        return;
      }

      const preparedOrders = await _getOrdersFromTradePath(
        executedTransactions.filter(isMultipleTransactionsPreparedOrder),
      );

      if (!preparedOrders.length) {
        setTransactions([]);
        return;
      }

      const lastOrder = preparedOrders[preparedOrders.length - 1];
      if (!lastOrder) {
        setTransactions(preparedOrders);
        return;
      }

      if (activeWithdrawal) {
        const preparedWithdrawal = _getWithdrawal(lastOrder.to.tokenSymbol, lastOrder.to.amount);
        setTransactions([...preparedOrders, preparedWithdrawal]);
      } else {
        setTransactions(preparedOrders);
      }
    } finally {
      setIsTxLoading(false);
    }
  };

  const executeNextTransaction = async () => {
    if (config?.isHub && !config?.defiAddress) {
      enqueueSnackbar("Defi address is not set. Please reconnect your wallet.", { variant: "warning" });
      return;
    }

    if (isTxLoading || !transactions.length) return;

    if (transactions.every((tx) => tx.isExecuted)) {
      if (accountId) {
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.ACCOUNT_BALANCES, accountId, auth.identityToken],
        });
        // Also invalidate automatic streak balance queries (they use a different query key)
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.AUTOMATIC_STREAK_BALANCE, accountId],
        });
      }
      onFinish?.();
      return;
    }

    setIsTxLoading(true);
    try {
      // Execute the transaction
      const transactionToExecute = transactions[txTracker];

      if (!transactionToExecute) return;

      if (isMultipleTransactionsPreparedOrder(transactionToExecute)) {
        // Execute the order
        let createOrderResponse: Order;
        try {
          if (!auth.identityToken) {
            throw new Error("Not authenticated");
          }

          const createOrderBody: HubTradeCreateOrderBody = {
            ...transactionToExecute.request,
            accountId: Number(accountId),
            campaignId: config?.campaignId || CAMPAIGN_PLACEHOLDER_NAME.TRADE_PAGE,
            hteCampaignId: config?.hteCampaignId,
            metadata: {
              ...transactionToExecute.request.metadata,
              defiAddress: config?.defiAddress,
            },
            amount: new Decimal(transactionToExecute.request.amount),
            price: new Decimal(transactionToExecute.request.price),
          };

          createOrderResponse = orderV2ToOrder(
            await apiHelper.postAuth(auth, API_ENDPOINT.HUB_TRADE_CREATE_ORDER, {
              bodyParams: createOrderBody,
              headers: {
                "sac-fetch-dest": "empty",
              },
            }),
          );

          verifyOrder(createOrderResponse.id);

          const [baseTokenSymbol] = getTokensFromPairSymbol(createOrderResponse.pairSymbol);
          const baseTokenPrice = getPrice(prices, baseTokenSymbol, account?.exchangeId);
          const baseTokenFiatPrice = baseTokenPrice ?? 0;
          const baseFiatAmount = createOrderResponse.amount * baseTokenFiatPrice;

          setTimeout(() => {
            config?.onTradeSubmitted?.({
              orderParams: transactionToExecute.request,
              order: createOrderResponse,
              exchangeId: account?.exchangeId,
              exchangeUserId: account?.exchangeUserId,
              baseFiatAmount,
            });
          }, 3000);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
          const errorMessage = getErrorMessage(error);
          config?.onTradeFailed?.({
            orderParams: transactionToExecute.request,
            exchangeId: account?.exchangeId,
            errorMessage,
          });
          enqueueSnackbar(`Trade failed: ${errorMessage}`, {
            variant: "error",
            incorrectPermission: error?.code === SdkErrorCodes.EXCHANGE_INVALID_PERMISSION,
          });
          throw error;
        }
        // Wait for the order to be filled
        const orderResponse = await _awaitOrderToBeFilled(createOrderResponse);

        // Update the portfolio before allowing the user to execute the next transaction
        // to ensure the balance is up to date for the internal transfer
        const transferWalletTypes = supportedExchangesById[account?.exchangeId ?? ""]?.transferWalletTypes;
        const balances = await getExchangeAccountBalances(auth, String(account?.id), transferWalletTypes || [], true);

        const transaction = orderToTransactionMapper({
          ...orderResponse,
          accountId: account?.id ?? "",
          exchangeId: account?.exchangeId ?? "",
        });

        // Update the transaction data for the CompletedTransaction page
        setExecutedTransactionData(transaction);

        // If the fees are in quote currency, we need to update the amount
        let updatedToAmount =
          orderResponse.fee.tokenSymbol === transaction.to.transaction.tokenSymbol
            ? transaction.to.transaction.amount - transaction.to.transaction.fee.amount
            : transaction.to.transaction.amount;

        updatedToAmount = _fitTxAmountToTokenFreeBalance(
          updatedToAmount,
          transaction.to.transaction.tokenSymbol,
          balances,
        );

        // Update transaction state
        const newTransactions = [...transactions];
        const transactionToUpdate = { ...newTransactions[txTracker] } as MultipleTransactionsPreparedOrder;
        transactionToUpdate.isExecuted = true;
        transactionToUpdate.to.amount = String(updatedToAmount);
        transactionToUpdate.from.amount = String(transaction.from.transaction.amount);
        newTransactions[txTracker] = transactionToUpdate;

        await _prepareTransactions(newTransactions.filter((tx) => tx.isExecuted));
        return;
      }

      // Withdrawal functionality has been removed - withdrawals are not supported on this platform
      throw new Error("Withdrawal transactions are not supported");
    } catch (e) {
      appendAlert(getErrorMessage(e), AlertTypes.ERROR);
    } finally {
      setIsTxLoading(false);
    }
  };

  return {
    /**
     * Is truthy when the transactions are computing or being executed
     */
    isTxLoading,
    transactions,
    executeNextTransaction,
    txTracker,
    clearState,
    loadTransactions: _prepareTransactions,
    /**
     * Store in memory the process has started (to redirect the user to the multiple transactions page
     * if the user refreshes the page)
     */
    startProcess,
    /**
     * Check if the process has started (to redirect the user to the multiple transactions page
     * if the user refreshes the page)
     */
    hasStartedTheProcess,
  };
};
